import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

/**
 * ItemMovementAnalysis
 *
 * Shows items bucketed into Fast Moving / Slow Moving / Non Moving.
 * Matches your API's three separate result sets — each bucket has its own
 * schema, so pass each one in directly (no client-side classification):
 *
 *   Fast / Slow moving rows: { ItemCode, ItemDescription, TotalSoldQty, TransactionCount }
 *   Non moving rows:         { ItemCode, ItemDescription, CurrentQty, AvgCost, StockValue }
 *
 * ── USAGE ────────────────────────────────────────────────────────────────
 *
 *   <ItemMovementAnalysis
 *     fastMovingItems={fastMovingItems}
 *     slowMovingItems={slowMovingItems}
 *     nonMovingItems={nonMovingItems}
 *     loading={loading}          // optional, while parent is fetching
 *     asOfDate="09 Jul 2026"      // optional label shown top-right
 *   />
 *
 * Parent is expected to fetch the three arrays (e.g. three calls similar to
 * your CRMStockReport endpoint, one per movement type) and pass them down as
 * plain arrays — this component only renders/filters, it doesn't fetch.
 */

const BUCKETS = [
  { key: "FAST", label: "Fast Moving", color: "#0F6E5F", soft: "#E4F2EE" },
  { key: "SLOW", label: "Slow Moving", color: "#B7791F", soft: "#FBF0DD" },
  { key: "NON", label: "Non Moving", color: "#B42318", soft: "#FBE7E5" },
];

// Fixed column schemas per bucket, matched to the real API response shapes.
const COLUMN_SETS = {
  FAST: [
    { key: "ItemCode", label: "Item Code", align: "left", mono: true },
    { key: "ItemDescription", label: "Description", align: "left", wrap: true },
    { key: "TotalSoldQty", label: "Qty Sold", align: "right", mono: true, decimals: 2 },
    { key: "TransactionCount", label: "Transactions", align: "right", mono: true, decimals: 0 },
  ],
  SLOW: [
    { key: "ItemCode", label: "Item Code", align: "left", mono: true },
    { key: "ItemDescription", label: "Description", align: "left", wrap: true },
    { key: "TotalSoldQty", label: "Qty Sold", align: "right", mono: true, decimals: 2 },
    { key: "TransactionCount", label: "Transactions", align: "right", mono: true, decimals: 0 },
  ],
  NON: [
    { key: "ItemCode", label: "Item Code", align: "left", mono: true },
    { key: "ItemDescription", label: "Description", align: "left", wrap: true },
    { key: "CurrentQty", label: "Current Qty", align: "right", mono: true, decimals: 2 },
    { key: "AvgCost", label: "Avg Cost", align: "right", mono: true, decimals: 2 },
    { key: "StockValue", label: "Stock Value", align: "right", mono: true, decimals: 2 },
  ],
};

function formatCell(value, col) {
  if (value === null || value === undefined || value === "") return "—";
  if (col.decimals !== undefined) {
    const n = Number(value);
    if (!isNaN(n)) {
      return n.toLocaleString(undefined, {
        minimumFractionDigits: col.decimals,
        maximumFractionDigits: col.decimals,
      });
    }
  }
  return String(value);
}

export default function StockMovement({
  fastMovingItems = [],
  slowMovingItems = [],
  nonMovingItems = [],
  loading = false,
  asOfDate = "",
}) {
  const [activeBucket, setActiveBucket] = useState(null); // null = modal closed
  const [search, setSearch] = useState("");

  const grouped = {
    FAST: fastMovingItems || [],
    SLOW: slowMovingItems || [],
    NON: nonMovingItems || [],
  };

  const activeItems = activeBucket ? grouped[activeBucket] || [] : [];
  const columns = activeBucket ? COLUMN_SETS[activeBucket] : [];

  const filteredItems = useMemo(() => {
    if (!search.trim()) return activeItems;
    const q = search.trim().toLowerCase();
    return activeItems.filter((row) =>
      Object.values(row).some((v) => String(v ?? "").toLowerCase().includes(q))
    );
  }, [activeItems, search]);

  const total = grouped.FAST.length + grouped.SLOW.length + grouped.NON.length;

  const openBucket = (key) => {
    setSearch("");
    setActiveBucket(key);
  };

  const closeModal = () => setActiveBucket(null);

  useEffect(() => {
    if (!activeBucket) return;
    function onKeyDown(e) {
      if (e.key === "Escape") closeModal();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [activeBucket]);

  return (
    <div
      style={{
        "--ink": "#152238",
        "--paper": "#F5F6F8",
        "--surface": "#FFFFFF",
        "--line": "#DCE1E8",
        "--accent": "#0F6E5F",
        "--accent-soft": "#E4F2EE",
        "--muted": "#5B6472",
      }}
      className="w-full text-[var(--ink)] font-sans px-2 pb-4"
    >
      <style>{`
        .mono { font-family: "IBM Plex Mono", "SFMono-Regular", Menlo, Consolas, monospace; font-variant-numeric: tabular-nums; }
        .move-table th, .move-table td { border-bottom: 1px solid var(--line); }
        .move-table tbody tr:hover { background: var(--accent-soft); }
      `}</style>

      <div className="flex items-baseline justify-between mb-3 p-0">
        <p className="text-xs text-gray-500 uppercase tracking-wider">
          Stock Movement Analysis
        </p>
        {/* {asOfDate && <span className="text-xs text-[var(--muted)] mono">{asOfDate}</span>} */}
      </div>

      {/* Summary cards — click to open table in a popup */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {BUCKETS.map((b) => {
          const count = grouped[b.key].length;
          const pct = total ? Math.round((count / total) * 100) : 0;
          return (
            <button
              key={b.key}
              onClick={() => openBucket(b.key)}
              className="text-left rounded-lg border p-3 transition hover:shadow-md"
              style={{ borderColor: "var(--line)", background: "var(--surface)" }}
            >
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ background: b.color }}
                />
                <span className="text-xs font-medium text-[var(--muted)]">{b.label}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold mono" style={{ color: b.color }}>
                  {count}
                </span>
                
              </div>

              <div
            style={{
              marginTop: 4,
              fontSize: 10,
              fontWeight: 500,
              color: "#94a3b8",
              display: "flex",
              alignItems: "center",
              gap: 3,
              whiteSpace: "nowrap",
            }}
          >
            <span>Click to view</span>
            <span style={{ fontSize: 10 }}>→</span>
          </div>
            </button>
          );
        })}
      </div>

      {/* Popup modal with the table for the clicked bucket */}
      {activeBucket &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(21,34,56,0.45)",
              "--ink": "#152238",
              "--paper": "#F5F6F8",
              "--surface": "#FFFFFF",
              "--line": "#DCE1E8",
              "--accent": "#0F6E5F",
              "--accent-soft": "#E4F2EE",
              "--muted": "#5B6472",
            }}
            onClick={closeModal}
          >
            <div
              className="bg-[var(--surface)] rounded-lg shadow-xl w-full max-w-4xl max-h-[75vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-[var(--line)]">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ background: BUCKETS.find((b) => b.key === activeBucket)?.color }}
                    />
                    <span className="font-semibold">
                      {BUCKETS.find((b) => b.key === activeBucket)?.label} ({filteredItems.length})
                    </span>
                  </span>
                  <button
                    onClick={closeModal}
                    aria-label="Close"
                    className="sm:hidden text-[var(--muted)] hover:text-[var(--ink)] transition text-xl leading-none px-1"
                  >
                    ×
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder="Search…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="border border-[var(--line)] rounded-md px-3 py-1.5 text-sm bg-white w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                  />
                  <button
                    onClick={closeModal}
                    aria-label="Close"
                    className="hidden sm:inline-block text-[var(--muted)] hover:text-[var(--ink)] transition text-xl leading-none px-1"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="overflow-auto flex-1">
                <table className="move-table w-full text-sm">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-[var(--ink)] text-white">
                      {columns.map((col) => (
                        <th
                          key={col.key}
                          className={`px-4 py-3 font-medium whitespace-nowrap ${col.align === "right" ? "text-right" : "text-left"}`}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={columns.length} className="px-4 py-10 text-center text-[var(--muted)]">
                          Loading…
                        </td>
                      </tr>
                    )}

                    {!loading && filteredItems.length === 0 && (
                      <tr>
                        <td colSpan={columns.length} className="px-4 py-10 text-center text-[var(--muted)]">
                          No items in this category.
                        </td>
                      </tr>
                    )}

                    {!loading &&
                      filteredItems.map((row, i) => (
                        <tr key={row.ItemCode ? `${row.ItemCode}-${i}` : i}>
                          {columns.map((col) => (
                            <td
                              key={col.key}
                              className={`px-4 py-2.5 ${col.align === "right" ? "text-right" : "text-left"} ${
                                col.mono ? "mono" : ""
                              } ${col.wrap ? "whitespace-normal min-w-[260px]" : "whitespace-nowrap"}`}
                            >
                              {formatCell(row[col.key], col)}
                            </td>
                          ))}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}